import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/ui/button";
import AttendanceModal from "@/features/education-management/components/AttendanceModal";
import type { Education } from "@/features/education-management/types/education-management.types";

import styles from "./InstructorAttendanceSection.module.scss";

interface InstructorAttendanceSectionProps {
  educationId: string;
  educationName: string;
}

/**
 * Eğitmenin yoklama alma bölümü.
 *
 * Yoklama altyapısı (tarih bazlı `EducationAttendance` + admin uçları) zaten
 * vardı ama yalnızca Eğitim Yöneticisi'nin "Sanal Sınıfı Düzenle" modalinden
 * açılabiliyordu; dersi asıl veren eğitmenin hiçbir yoklama ekranı yoktu
 * (28.08.2026 bildirimi, 4. madde). Aynı modal burada eğitmene açılır.
 *
 * Çok günlü eğitimlerde yoklama gün bazlıdır: modal içindeki tarih seçicisi her
 * oturum günü için ayrı kayıt tutar, sanal sınıf seçicisi de çok sınıflı
 * eğitimlerde grubu daraltır. Online/yüz yüze ayrımı yoktur — yoklama uçları
 * eğitim tipinden bağımsız çalışır.
 */
export const InstructorAttendanceSection: React.FC<InstructorAttendanceSectionProps> = ({
  educationId,
  educationName,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // AttendanceModal admin tarafının `Education` tipini bekliyor; kursiyer/eğitmen
  // detayında o gövdenin tamamı yok. Modal yalnızca `id` ve `educationName`
  // alanlarını okuduğu için asgari nesne yeterli.
  const attendanceEducation = useMemo<Education>(
    () =>
      ({
        id: educationId,
        educationCode: "",
        educationName,
        educatorName: "",
        format: "canli",
        targetAudience: 0,
        registrationCount: 0,
        publishDate: "",
        status: "aktif",
        createdAt: "",
        updatedAt: "",
      }) satisfies Education,
    [educationId, educationName],
  );

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t("instructorAttendance.title", "Yoklama")}</h2>
        <p className={styles.subtitle}>
          {t(
            "instructorAttendance.subtitle",
            "Her oturum günü için ayrı yoklama alabilirsiniz. Tarihi ve sanal sınıfı açılan ekrandan seçin.",
          )}
        </p>
      </div>
      <Button color="primary" onClick={() => setIsOpen(true)} type="button" variant="contained">
        {t("instructorAttendance.take", "Yoklama Al")}
      </Button>

      <AttendanceModal
        education={attendanceEducation}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </section>
  );
};

export default InstructorAttendanceSection;
